<template>
  <Teleport to="body">
    <TransitionGroup name="overlay">
      <div
        v-for="overlay in overlays"
        :key="overlay.id"
        class="overlay-wrapper"
        :style="{ zIndex: overlay.zIndex }"
      >
        <!-- Backdrop -->
        <Transition name="fade">
          <div
            v-if="overlay.backdrop && overlay.visible"
            class="overlay-backdrop"
            :style="{ zIndex: overlay.zIndex }"
            @click="handleBackdropClick(overlay)"
          />
        </Transition>

        <!-- Overlay Content -->
        <Transition :name="getTransitionName(overlay)">
          <div
            v-if="overlay.visible"
            :class="getOverlayClasses(overlay)"
            :style="{ zIndex: overlay.zIndex + 1 }"
          >
            <component
              :is="overlay.component"
              v-bind="overlay.props"
              v-on="overlay.events || {}"
              @close="(data) => closeOverlay(overlay.id, data)"
            />
          </div>
        </Transition>
      </div>
    </TransitionGroup>
  </Teleport>
</template>

<script>
import { computed } from 'vue';
import { useOverlay } from '@/services/overlay-service';

export default {
  name: 'OverlayContainer',
  setup() {
    const { state, close } = useOverlay();

    const overlays = computed(() => state.overlays);

    const handleBackdropClick = (overlay) => {
      if (overlay.closeOnBackdrop) {
        close(overlay.id);
      }
    };

    const closeOverlay = (id, data) => {
      close(id, data);
    };

    const getTransitionName = (overlay) => {
      if (overlay.type === 'sidebar') {
        return overlay.position === 'left' ? 'slide-left' : 'slide-right';
      }
      return 'modal';
    };

    const getOverlayClasses = (overlay) => {
      const classes = ['overlay-content'];

      if (overlay.type === 'modal') {
        classes.push('overlay-modal');
        classes.push(`overlay-modal-${overlay.position}`);
        classes.push(`overlay-modal-${overlay.size}`);
      } else if (overlay.type === 'sidebar') {
        classes.push('overlay-sidebar');
        classes.push(`overlay-sidebar-${overlay.position}`);
        classes.push(`overlay-sidebar-${overlay.size}`);
      }

      return classes;
    };

    return {
      overlays,
      handleBackdropClick,
      closeOverlay,
      getTransitionName,
      getOverlayClasses
    };
  }
};
</script>

<style lang="scss" scoped>
.overlay-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
}

.overlay-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.overlay-content {
  position: fixed;
  pointer-events: auto;
}

/* Modal Styles */
.overlay-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  &.overlay-modal-center {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &.overlay-modal-top {
    top: 2rem;
    left: 50%;
    transform: translateX(-50%);
  }

  &.overlay-modal-bottom {
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
  }

  &.overlay-modal-sm {
    width: 100%;
    max-width: 24rem;
  }

  &.overlay-modal-md {
    width: 100%;
    max-width: 32rem;
  }

  &.overlay-modal-lg {
    width: 100%;
    max-width: 48rem;
  }

  &.overlay-modal-xl {
    width: 100%;
    max-width: 64rem;
  }

  &.overlay-modal-full {
    width: calc(100% - 2rem);
    height: calc(100% - 2rem);
    max-width: none;
  }
}

/* Sidebar Styles */
.overlay-sidebar {
  top: 0;
  height: 100%;
  background-color: white;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow-y: auto;

  &.overlay-sidebar-left {
    left: 0;
  }

  &.overlay-sidebar-right {
    right: 0;
  }

  /* Mobile first - por defecto 100% */
  &.overlay-sidebar-sm {
    width: 100%;
  }

  &.overlay-sidebar-md {
    width: 100%;
  }

  &.overlay-sidebar-lg {
    width: 100%;
  }

  &.overlay-sidebar-xl {
    width: 100%;
  }

  &.overlay-sidebar-full {
    width: 100%;
  }

  /* Tablet - 768px en adelante (60%) */
  @media (min-width: 768px) {
    &.overlay-sidebar-sm {
      width: 60%;
      max-width: 24rem; /* 384px */
    }

    &.overlay-sidebar-md {
      width: 60%;
      max-width: 32rem; /* 512px */
    }

    &.overlay-sidebar-lg {
      width: 60%;
    }

    &.overlay-sidebar-xl {
      width: 60%;
    }

    &.overlay-sidebar-full {
      width: 100%;
    }
  }

  /* Desktop - 1024px en adelante (40%) */
  @media (min-width: 1024px) {
    &.overlay-sidebar-sm {
      width: 40%;
      max-width: 20rem; /* 320px */
    }

    &.overlay-sidebar-md {
      width: 40%;
      max-width: 28rem; /* 448px */
    }

    &.overlay-sidebar-lg {
      width: 40%;
    }

    &.overlay-sidebar-xl {
      width: 40%;
      max-width: 40rem; /* 640px */
    }

    &.overlay-sidebar-full {
      width: 100%;
    }
  }

  /* Large Desktop - 1280px en adelante (ajuste fino) */
  @media (min-width: 1280px) {
    &.overlay-sidebar-sm {
      width: 35%;
      max-width: 20rem;
    }

    &.overlay-sidebar-md {
      width: 35%;
      max-width: 28rem;
    }

    &.overlay-sidebar-lg {
      width: 35%;
    }

    &.overlay-sidebar-xl {
      width: 35%;
      max-width: 40rem;
    }
  }

  /* Extra Large Desktop - 1536px en adelante */
  @media (min-width: 1536px) {
    &.overlay-sidebar-sm {
      width: 30%;
      max-width: 20rem;
    }

    &.overlay-sidebar-md {
      width: 30%;
      max-width: 28rem;
    }

    &.overlay-sidebar-lg {
      width: 30%;
    }

    &.overlay-sidebar-xl {
      width: 30%;
      max-width: 40rem;
    }
  }
}

/* Transition Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
  transform: translate(-50%, -48%) scale(0.95);
}

.modal-leave-to {
  opacity: 0;
  transform: translate(-50%, -52%) scale(0.95);
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(-100%);
}

.slide-left-leave-to {
  transform: translateX(-100%);
}

.slide-right-enter-from {
  transform: translateX(100%);
}

.slide-right-leave-to {
  transform: translateX(100%);
}

.overlay-enter-active,
.overlay-leave-active {
  transition: all 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}
</style>